import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, CalendarClock, Package, Pencil, Plus, Trash2 } from "lucide-react"
import { fr } from "../../locales/fr"
import { getPacks, createPack, updatePack, deletePack } from "../../services/packService"
import {
  getSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
} from "../../services/subscriptionPlanService"
import { getPackPlanPricesMatrix, savePackPlanPrices } from "../../services/packPlanPriceService"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { EmptyState } from "../../components/ui/EmptyState"
import { TableSkeleton } from "../../components/ui/Skeleton"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/Table"

const textareaClass = `
  flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground
  placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
`

export default function SettingsPacks() {
  const navigate = useNavigate()
  const sp = fr.settings.packs
  const [packs, setPacks] = useState([])
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [pageError, setPageError] = useState("")
  const [pricingCells, setPricingCells] = useState([])
  const [savingMatrix, setSavingMatrix] = useState(false)

  const [packModalOpen, setPackModalOpen] = useState(false)
  const [packEditingId, setPackEditingId] = useState(null)
  const [packForm, setPackForm] = useState({ name: "", price: "", description: "" })
  const [savingPack, setSavingPack] = useState(false)

  const [planModalOpen, setPlanModalOpen] = useState(false)
  const [planEditingId, setPlanEditingId] = useState(null)
  const [planForm, setPlanForm] = useState({
    name: "",
    durationMonths: "",
    description: "",
  })
  const [savingPlan, setSavingPlan] = useState(false)

  const [modalError, setModalError] = useState("")

  const load = async () => {
    setLoading(true)
    setPageError("")
    try {
      const [p, pl, matrix] = await Promise.all([
        getPacks(),
        getSubscriptionPlans(),
        getPackPlanPricesMatrix(),
      ])
      setPacks(Array.isArray(p) ? p : [])
      setPlans(Array.isArray(pl) ? pl : [])
      setPricingCells(Array.isArray(matrix?.cells) ? matrix.cells : [])
    } catch (e) {
      setPageError(sp.loadError)
      setPacks([])
      setPlans([])
      setPricingCells([])
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const openCreatePack = () => {
    setModalError("")
    setPackEditingId(null)
    setPackForm({ name: "", price: "", description: "" })
    setPackModalOpen(true)
  }

  const openEditPack = (row) => {
    setModalError("")
    setPackEditingId(row.id)
    setPackForm({
      name: row.name || "",
      price: String(row.price ?? ""),
      description: row.description || "",
    })
    setPackModalOpen(true)
  }

  const closePackModal = () => {
    setPackModalOpen(false)
    setPackEditingId(null)
  }

  const savePack = async () => {
    const name = packForm.name.trim()
    const price = parseFloat(String(packForm.price).replace(",", "."), 10)
    if (!name) {
      setModalError(sp.nameRequired)
      return
    }
    if (!Number.isFinite(price) || price < 0) {
      setModalError(sp.priceInvalid)
      return
    }
    setSavingPack(true)
    setModalError("")
    try {
      const payload = {
        name,
        price,
        description: packForm.description.trim() || null,
      }
      if (packEditingId == null) {
        await createPack(payload)
      } else {
        await updatePack(packEditingId, payload)
      }
      closePackModal()
      await load()
    } catch (e) {
      const msg = e?.response?.data?.error
      setModalError(typeof msg === "string" ? msg : sp.saveError)
    } finally {
      setSavingPack(false)
    }
  }

  const removePack = async (row) => {
    if (!window.confirm(sp.confirmDelete)) return
    setPageError("")
    try {
      await deletePack(row.id)
      await load()
    } catch (e) {
      const msg = e?.response?.data?.error
      setPageError(typeof msg === "string" ? msg : sp.deleteError)
    }
  }

  const openCreatePlan = () => {
    setModalError("")
    setPlanEditingId(null)
    setPlanForm({ name: "", durationMonths: "", description: "" })
    setPlanModalOpen(true)
  }

  const openEditPlan = (row) => {
    setModalError("")
    setPlanEditingId(row.id)
    setPlanForm({
      name: row.name || "",
      durationMonths: String(row.durationMonths ?? ""),
      description: row.description || "",
    })
    setPlanModalOpen(true)
  }

  const closePlanModal = () => {
    setPlanModalOpen(false)
    setPlanEditingId(null)
  }

  const savePlan = async () => {
    const name = planForm.name.trim()
    const durationMonths = parseInt(String(planForm.durationMonths).trim(), 10)
    if (!name) {
      setModalError(sp.planNameRequired)
      return
    }
    if (!Number.isFinite(durationMonths) || durationMonths < 1) {
      setModalError(sp.planDurationInvalid)
      return
    }
    setSavingPlan(true)
    setModalError("")
    try {
      const payload = {
        name,
        duration_months: durationMonths,
        description: planForm.description.trim() || null,
      }
      if (planEditingId == null) {
        await createSubscriptionPlan(payload)
      } else {
        await updateSubscriptionPlan(planEditingId, payload)
      }
      closePlanModal()
      await load()
    } catch (e) {
      const msg = e?.response?.data?.error
      setModalError(typeof msg === "string" ? msg : sp.planSaveError)
    } finally {
      setSavingPlan(false)
    }
  }

  const removePlan = async (row) => {
    if (!window.confirm(sp.planConfirmDelete)) return
    setPageError("")
    try {
      await deleteSubscriptionPlan(row.id)
      await load()
    } catch (e) {
      const msg = e?.response?.data?.error
      setPageError(typeof msg === "string" ? msg : sp.planDeleteError)
    }
  }

  const updateMatrixCell = (packId, planId, raw) => {
    const value = parseFloat(String(raw).replace(",", "."), 10)
    const price = Number.isFinite(value) && value >= 0 ? value : 0
    setPricingCells((prev) =>
      prev.map((c) =>
        c.packId === packId && c.planId === planId ? { ...c, price } : c
      )
    )
  }

  const saveMatrix = async () => {
    setSavingMatrix(true)
    setPageError("")
    try {
      await savePackPlanPrices(pricingCells)
      await load()
    } catch (e) {
      const msg = e?.response?.data?.error
      setPageError(typeof msg === "string" ? msg : sp.matrixSaveError)
    } finally {
      setSavingMatrix(false)
    }
  }

  return (
    <div className="space-y-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Button variant="outline" size="sm" type="button" onClick={() => navigate("/settings")}>
            <ArrowLeft className="h-4 w-4" />
            {sp.backToSettings}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{sp.title}</h1>
            <p className="text-muted-foreground">{sp.subtitle}</p>
          </div>
        </div>
      </div>

      {pageError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {pageError}
        </div>
      ) : null}

      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{sp.sectionPacksTitle}</h2>
            <p className="text-sm text-muted-foreground">{sp.sectionPacksDescription}</p>
          </div>
          <Button type="button" className="shrink-0 self-start" onClick={openCreatePack}>
            <Plus className="mr-2 h-4 w-4" />
            {sp.addPack}
          </Button>
        </div>

        <Card>
          {loading ? (
            <CardContent className="p-6">
              <TableSkeleton rows={5} columns={4} />
            </CardContent>
          ) : packs.length === 0 ? (
            <CardContent className="py-12">
              <EmptyState
                icon={Package}
                title={sp.emptyTitle}
                description={sp.emptyDescription}
                action={openCreatePack}
                actionLabel={sp.addPack}
              />
            </CardContent>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{sp.tableName}</TableHead>
                  <TableHead className="text-right">{sp.tablePrice}</TableHead>
                  <TableHead>{sp.tableDescription}</TableHead>
                  <TableHead className="w-[120px] text-right">{fr.common.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {packs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(row.price).toLocaleString()} MAD
                    </TableCell>
                    <TableCell className="max-w-md truncate text-muted-foreground">
                      {row.description || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => openEditPack(row)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removePack(row)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{sp.sectionPlansTitle}</h2>
            <p className="text-sm text-muted-foreground">{sp.sectionPlansDescription}</p>
          </div>
          <Button type="button" className="shrink-0 self-start" onClick={openCreatePlan}>
            <Plus className="mr-2 h-4 w-4" />
            {sp.addPlan}
          </Button>
        </div>

        <Card>
          {loading ? (
            <CardContent className="p-6">
              <TableSkeleton rows={5} columns={4} />
            </CardContent>
          ) : plans.length === 0 ? (
            <CardContent className="py-12">
              <EmptyState
                icon={CalendarClock}
                title={sp.planEmptyTitle}
                description={sp.planEmptyDescription}
                action={openCreatePlan}
                actionLabel={sp.addPlan}
              />
            </CardContent>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{sp.tableName}</TableHead>
                  <TableHead className="text-right">{sp.planTableDuration}</TableHead>
                  <TableHead>{sp.tableDescription}</TableHead>
                  <TableHead className="w-[120px] text-right">{fr.common.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.durationMonths} {sp.planMonthsSuffix}
                    </TableCell>
                    <TableCell className="max-w-md truncate text-muted-foreground">
                      {row.description || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => openEditPlan(row)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removePlan(row)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{sp.matrixTitle}</h2>
          <p className="text-sm text-muted-foreground">{sp.matrixHint}</p>
        </div>
        <Card>
          {loading ? (
            <CardContent className="p-6">
              <TableSkeleton rows={4} columns={4} />
            </CardContent>
          ) : packs.length === 0 || plans.length === 0 ? (
            <CardContent className="py-8 text-center text-sm text-muted-foreground">{sp.matrixNeedBoth}</CardContent>
          ) : (
            <>
              <div className="overflow-x-auto p-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[140px] whitespace-nowrap">{sp.matrixPackColumn}</TableHead>
                      {plans.map((pl) => (
                        <TableHead key={pl.id} className="min-w-[100px] text-right whitespace-nowrap">
                          {pl.name}
                          <span className="block text-xs font-normal text-muted-foreground">
                            ({pl.durationMonths} {sp.planMonthsSuffix})
                          </span>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packs.map((pack) => (
                      <TableRow key={pack.id}>
                        <TableCell className="font-medium whitespace-nowrap">
                          {pack.name}
                          <span className="block text-xs font-normal text-muted-foreground">
                            {sp.tablePrice}: {Number(pack.price).toLocaleString()} MAD
                          </span>
                        </TableCell>
                        {plans.map((pl) => {
                          const cell = pricingCells.find(
                            (c) => c.packId === pack.id && c.planId === pl.id
                          )
                          const val = cell ? String(cell.price) : "0"
                          return (
                            <TableCell key={pl.id} className="p-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                className="h-9 w-full min-w-[88px] tabular-nums text-right"
                                value={val}
                                onChange={(e) => updateMatrixCell(pack.id, pl.id, e.target.value)}
                                aria-label={`${pack.name} — ${pl.name}`}
                              />
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <CardContent className="flex justify-end border-t border-border pt-4">
                <Button type="button" onClick={saveMatrix} disabled={savingMatrix}>
                  {savingMatrix ? fr.common.saving : sp.matrixSave}
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </section>

      {packModalOpen ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            aria-label={fr.common.close}
            onClick={closePackModal}
          />
          <Card className="relative z-10 w-full max-w-md border-border shadow-lg">
            <CardHeader>
              <CardTitle>{packEditingId == null ? sp.modalCreateTitle : sp.modalEditTitle}</CardTitle>
              <CardDescription>{sp.modalHint}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {modalError ? <p className="text-sm text-destructive">{modalError}</p> : null}
              <div className="space-y-2">
                <Label htmlFor="pack-name">{sp.fieldName}</Label>
                <Input
                  id="pack-name"
                  value={packForm.name}
                  onChange={(e) => setPackForm({ ...packForm, name: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pack-price">{sp.fieldPrice}</Label>
                <Input
                  id="pack-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={packForm.price}
                  onChange={(e) => setPackForm({ ...packForm, price: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pack-desc">{sp.fieldDescription}</Label>
                <textarea
                  id="pack-desc"
                  className={textareaClass}
                  rows={3}
                  value={packForm.description}
                  onChange={(e) => setPackForm({ ...packForm, description: e.target.value })}
                  placeholder={sp.descriptionPlaceholder}
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closePackModal}>
                  {fr.common.cancel}
                </Button>
                <Button type="button" onClick={savePack} disabled={savingPack}>
                  {savingPack ? fr.common.saving : fr.common.save}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {planModalOpen ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            aria-label={fr.common.close}
            onClick={closePlanModal}
          />
          <Card className="relative z-10 w-full max-w-md border-border shadow-lg">
            <CardHeader>
              <CardTitle>{planEditingId == null ? sp.planModalCreateTitle : sp.planModalEditTitle}</CardTitle>
              <CardDescription>{sp.planModalHint}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {modalError ? <p className="text-sm text-destructive">{modalError}</p> : null}
              <div className="space-y-2">
                <Label htmlFor="plan-name">{sp.planFieldName}</Label>
                <Input
                  id="plan-name"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plan-duration">{sp.planFieldDuration}</Label>
                <Input
                  id="plan-duration"
                  type="number"
                  min="1"
                  step="1"
                  value={planForm.durationMonths}
                  onChange={(e) => setPlanForm({ ...planForm, durationMonths: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plan-desc">{sp.planFieldDescription}</Label>
                <textarea
                  id="plan-desc"
                  className={textareaClass}
                  rows={3}
                  value={planForm.description}
                  onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                  placeholder={sp.planDescriptionPlaceholder}
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closePlanModal}>
                  {fr.common.cancel}
                </Button>
                <Button type="button" onClick={savePlan} disabled={savingPlan}>
                  {savingPlan ? fr.common.saving : fr.common.save}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  )
}
